import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useState } from 'react';
import { RotateCcw, AlertTriangle } from 'lucide-react';
import { hardwareApi, type DeviceUnitRow } from '../../services/hardwareApi';
import { toastService } from '../../services/toastService';

/**
 * SEND THIS TILL BACK TO BE BUILT AGAIN.
 *
 * One component for both places this is offered — the Fleet table and the
 * order's own unit list — because they are the same decision made from two
 * screens, and a confirm that reads differently in one of them is how an
 * operator learns to skim it.
 *
 * WHAT IT COSTS is stated before it is pressed, not after: the checklist goes
 * back to nothing, and everything already ticked has to be ticked again. That
 * is the point (the ticks were recorded against the build being replaced) but
 * it is real work, and an operator who did not know is entitled to be annoyed.
 *
 * The reason is optional and free text. It is shown in Pending Configuration
 * beside the unit, so the next person picking it up knows what to do
 * differently rather than reinstalling the same thing the same way.
 */

interface Props {
  unitId: string;
  /** What the operator calls this device — tracking id, serial, register name. */
  label: string;
  /** Where it is now, so the confirm can name what is being undone. */
  fromState?: string;
  /** How many checklist steps are about to be discarded, when known. */
  ticked?: number;
  onClose: () => void;
  /** The updated unit, so a caller that can patch one row does not have to
   *  refetch the whole order and remount the stage rail. */
  onDone: (unit: DeviceUnitRow) => void;
}

const ReinstallModal: React.FC<Props> = ({ unitId, label, fromState, ticked, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const res = await hardwareApi.requestReinstall(unitId, reason.trim() || undefined);
      toastService.success(`${label} is back in Pending Configuration — install the build, then run its checklist again.`);
      onClose();
      onDone(res.unit);
    } catch (e: any) {
      /* The server's message names what to do instead — a shipped device has
         to be recorded as returned before it can be reinstalled. Surfaced
         verbatim rather than replaced with a generic failure. */
      toastService.error(e?.message || 'Could not request a reinstall.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-[480px] overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-stone-200 px-5 py-4">
          <h3 className="text-[15px] font-bold text-stone-900">Reinstall this till?</h3>
          <p className="mt-1 text-[12.5px] text-stone-500">{label}</p>
        </div>

        <div className="space-y-3 px-5 py-4">
          <div className="text-[12.5px] leading-relaxed text-stone-600">
            It goes back to <b>Pending Configuration</b>
            {fromState ? <> from <b>{fromState}</b></> : null}, ready to be installed again.
          </div>

          {/* The cost, before the button, in the operator's own terms. */}
          <div className="rounded-lg border-l-[3px] border-amber-400 bg-amber-50 px-3 py-2.5 text-[11.5px] leading-relaxed text-amber-900">
            <p className="flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span>
                {ticked ? <>Its <b>{ticked} completed checklist step{ticked === 1 ? '' : 's'}</b> will be cleared</> : 'Its provisioning checklist will be cleared'}
                {' '}and has to be run again — the ticks recorded work against the build you are replacing.
              </span>
            </p>
            <p className="mt-1.5 pl-[18px]">
              The install itself is done over adb from this machine, so the device has to be on the bench or on this network.
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-[12px] font-semibold text-stone-700">
              Why? <span className="font-normal text-stone-400">optional — shown in the queue</span>
            </label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 200))}
              placeholder="e.g. installed from the wrong channel"
              className="min-h-[44px] w-full rounded-lg border border-stone-300 px-3 text-[13px] text-stone-900 placeholder-stone-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-rose-500"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3.5">
          <button
            onClick={onClose}
            className="min-h-[44px] rounded-xl border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50"
          >
            Cancel
          </button>
          <button
            onClick={run}
            disabled={busy}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? <RubyGemMark size={14} drawing color="currentColor" /> : <RotateCcw size={14} />}
            Send back to configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReinstallModal;
